'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type ResizePreset = {
  id: string;
  label: string;
  width: number;
  height: number;
  hint: string;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const presets: ResizePreset[] = [
  {
    id: 'square',
    label: 'Square',
    width: 1080,
    height: 1080,
    hint: 'Good for profile photos and square posts.',
  },
  {
    id: 'portrait',
    label: 'Portrait',
    width: 1080,
    height: 1350,
    hint: 'A clean fit for portrait images and social posts.',
  },
  {
    id: 'story',
    label: 'Story',
    width: 1080,
    height: 1920,
    hint: 'Useful for tall mobile-first layouts and story formats.',
  },
  {
    id: 'landscape',
    label: 'Landscape',
    width: 1600,
    height: 900,
    hint: 'A simple fit for wide covers and banners.',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    width: 400,
    height: 400,
    hint: 'A safe square size for profile-style uploads.',
  },
  {
    id: 'resume',
    label: 'Resume',
    width: 1200,
    height: 1500,
    hint: 'A portrait export that works well for cleaner professional photos.',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getCropRect(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const aspect = targetWidth / targetHeight;
  const imageAspect = imageWidth / imageHeight;

  let baseCropWidth = imageWidth;
  let baseCropHeight = imageHeight;

  if (imageAspect > aspect) {
    baseCropHeight = imageHeight;
    baseCropWidth = imageHeight * aspect;
  } else {
    baseCropWidth = imageWidth;
    baseCropHeight = imageWidth / aspect;
  }

  const cropWidth = baseCropWidth / zoom;
  const cropHeight = baseCropHeight / zoom;
  const maxOffsetX = Math.max(0, (imageWidth - cropWidth) / 2);
  const maxOffsetY = Math.max(0, (imageHeight - cropHeight) / 2);
  const cropX = clamp(
    (imageWidth - cropWidth) / 2 + offsetX * maxOffsetX,
    0,
    imageWidth - cropWidth,
  );
  const cropY = clamp(
    (imageHeight - cropHeight) / 2 + offsetY * maxOffsetY,
    0,
    imageHeight - cropHeight,
  );

  return {
    cropX,
    cropY,
    cropWidth,
    cropHeight,
  };
}

function drawCroppedImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const { cropX, cropY, cropWidth, cropHeight } = getCropRect(
    image.naturalWidth,
    image.naturalHeight,
    targetWidth,
    targetHeight,
    zoom,
    offsetX,
    offsetY,
  );

  context.clearRect(0, 0, targetWidth, targetHeight);
  context.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight);
}

function getPreviewSize(targetWidth: number, targetHeight: number) {
  const aspect = targetWidth / targetHeight;
  let width = 900;
  let height = Math.round(width / aspect);

  if (height > 420) {
    height = 420;
    width = Math.round(height * aspect);
  }

  return { width, height };
}

export default function BrowserImageResizer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activePresetId, setActivePresetId] = useState<string>(presets[0].id);
  const [targetWidth, setTargetWidth] = useState<number>(presets[0].width);
  const [targetHeight, setTargetHeight] = useState<number>(presets[0].height);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.92);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [exportedSize, setExportedSize] = useState<number | null>(null);

  const activePreset = presets.find((preset) => preset.id === activePresetId) ?? null;
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const previewSize = useMemo(
    () => getPreviewSize(targetWidth, targetHeight),
    [targetWidth, targetHeight],
  );

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  useEffect(() => {
    const canvas = previewCanvasRef.current;

    if (!canvas || !loadedImage || targetWidth < 1 || targetHeight < 1) {
      return;
    }

    canvas.width = previewSize.width;
    canvas.height = previewSize.height;

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    drawCroppedImage(
      context,
      loadedImage.element,
      previewSize.width,
      previewSize.height,
      zoom,
      offsetX,
      offsetY,
    );
  }, [
    loadedImage,
    offsetX,
    offsetY,
    previewSize.height,
    previewSize.width,
    targetHeight,
    targetWidth,
    zoom,
  ]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setDownloadUrl(null);
    setDownloadName(null);
    setExportedSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setZoom(1);
    setOffsetX(0);
    setOffsetY(0);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    try {
      const image = await loadImageFromFile(file);
      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    }
  }

  function applyPreset(preset: ResizePreset) {
    setActivePresetId(preset.id);
    setTargetWidth(preset.width);
    setTargetHeight(preset.height);
    setZoom(1);
    setOffsetX(0);
    setOffsetY(0);
    resetOutputState();
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'resizing-cropping' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawCroppedImage(
        context,
        loadedImage.element,
        targetWidth,
        targetHeight,
        zoom,
        offsetX,
        offsetY,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The export could not finish for this image.');
      }

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-${targetWidth}x${targetHeight}.${selectedOutput.extension}`;

      setDownloadUrl(nextDownloadUrl);
      setDownloadName(nextFileName);
      setExportedSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The image could not be exported.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Resize and crop in the browser</h2>
          <p>Upload one image, choose the size, adjust the framing, and export it right away.</p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Popular size presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            className={`tool-live-route ${activePresetId === preset.id ? 'tool-live-route-active' : ''}`}
            onClick={() => applyPreset(preset)}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{loadedImage ? loadedImage.name : 'Choose an image or drop it here'}</strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-preview tool-editor-preview">
            <h3>Preview</h3>
            <div className="tool-live-preview-frame tool-editor-preview-frame">
              {loadedImage ? (
                <canvas ref={previewCanvasRef} className="tool-editor-canvas" />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Preview appears here</strong>
                  <span>Upload an image to adjust the framing before export.</span>
                </div>
              )}
            </div>
          </div>

          <div className="tool-live-controls">
            <label className="field">
              <span>Width</span>
              <input
                min="64"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setTargetWidth(Math.max(64, Number(event.target.value) || 64));
                  resetOutputState();
                }}
                type="number"
                value={targetWidth}
              />
            </label>
            <label className="field">
              <span>Height</span>
              <input
                min="64"
                onChange={(event) => {
                  setActivePresetId('custom');
                  setTargetHeight(Math.max(64, Number(event.target.value) || 64));
                  resetOutputState();
                }}
                type="number"
                value={targetHeight}
              />
            </label>
            <label className="field">
              <span>Format</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Preset</span>
              <input readOnly type="text" value={activePreset?.label ?? 'Custom'} />
            </label>
          </div>

          <div className="tool-live-quality-shell tool-editor-sliders">
            <label className="field tool-live-quality">
              <span>Zoom</span>
              <input
                max="3"
                min="1"
                onChange={(event) => {
                  setZoom(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={zoom}
              />
              <small>{zoom.toFixed(2)}×</small>
            </label>
            <label className="field tool-live-quality">
              <span>Horizontal</span>
              <input
                max="1"
                min="-1"
                onChange={(event) => {
                  setOffsetX(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={offsetX}
              />
              <small>Move left or right inside the frame</small>
            </label>
            <label className="field tool-live-quality">
              <span>Vertical</span>
              <input
                max="1"
                min="-1"
                onChange={(event) => {
                  setOffsetY(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={offsetY}
              />
              <small>Move up or down inside the frame</small>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.4"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.02"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `Used for ${selectedOutput.label} exports`
                  : 'PNG keeps the clean export without a quality slider'}
              </small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isExporting}
              onClick={() => void runExport()}
              type="button"
            >
              {isExporting ? 'Exporting…' : `Export ${targetWidth} × ${targetHeight}`}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download {selectedOutput.label}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Resize for common destinations</h3>
          <div className="tool-mockup-chip-list">
            <span>Square</span>
            <span>Portrait</span>
            <span>Story</span>
            <span>LinkedIn</span>
            <span>Resume</span>
          </div>
          <p>
            {activePreset?.hint ??
              'Use presets for a quick start, or set your own width and height.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to PNG, JPG, or WebP.</span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{normalizeSourceLabel(loadedImage.type)}</span>
              </div>
              <div>
                <strong>Resolution</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Export</strong>
                <span>{`${targetWidth} × ${targetHeight}`}</span>
              </div>
              {exportedSize ? (
                <div>
                  <strong>Result</strong>
                  <span>{formatFileSize(exportedSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  );
}
